import classNames from "classnames";
import React, { useEffect, useRef, useState } from "react";
import { isEmpty } from "../../common/utils/string.util";
import "../LoadingSpinner/LoadingSpinner.css";
import "./EditableTextarea.css";
import LoadingSpinner from "../LoadingSpinner/LoadingSpinner";
import InternalIndicator from "./InternalIndicator";

type Props = {
    label?: string;
    placeholder?: string;
    initialValue?: string | null;
    className?: string;
    height?: number;
    maxHeight?: number;
    direction?: "row" | "column";
    internalIndicator?: boolean;
    allowEmpty?: boolean;
    triggerMultipleOnChange?: boolean;
    onChange?: (value: string) => void | Promise<any>;
    highlight?: boolean;
};

const EditableTextArea = function EditableTextArea(props: Props) {
    const {
        label,
        placeholder,
        className,
        height,
        maxHeight,
        direction,
        internalIndicator,
        initialValue,
        allowEmpty,
        triggerMultipleOnChange,
        onChange,
        highlight,
    } = props;
    const [value, setValue] = useState<string>(initialValue || "");
    const [hasBeenEdited, setHasBeenEdited] = useState(false);
    const [isUpdating, setIsUpdating] = useState(false);
    const ref = useRef<HTMLTextAreaElement | null>(null);
    const defaultMaxHeight = 300;

    function autoResize() {
        if (ref.current) {
            ref.current.style.height = "auto";
            ref.current.style.height = `${ref.current.scrollHeight}px`;
        }
    }

    const triggerChanged = async () => {
        if (hasBeenEdited && onChange && (allowEmpty || value.length > 0)) {
            try {
                setIsUpdating(true);
                await onChange(value);
            } catch {
                setValue(initialValue || "");
            }
            setIsUpdating(false);
            setHasBeenEdited(false);
        }
        if (!allowEmpty && value.length === 0) {
            setValue(initialValue || "");
        }
    };

    useEffect(() => {
        if (triggerMultipleOnChange && !isEmpty(value)) triggerChanged();
    }, [value]);

    useEffect(() => {
        setValue(initialValue || "");
    }, [initialValue]);

    return (
        <div
            className={classNames(
                "editable-textarea",
                className,
                `editable-textarea--${direction}`,
            )}
        >
            {label ? (
                <label className="editable-textarea__label">
                    {label}
                    {internalIndicator && <InternalIndicator />}
                </label>
            ) : (
                ""
            )}
            <div className="editable-textarea__input-container">
                {isUpdating && <LoadingSpinner.Medium.AbsoluteTopRight />}
                <textarea
                    ref={(el) => {
                        ref.current = el;
                        autoResize();
                    }}
                    style={{
                        minHeight: height,
                        maxHeight: maxHeight || defaultMaxHeight,
                    }}
                    autoComplete="off"
                    className={classNames("editable-textarea__input", {
                        "!border-blue-200 !bg-blue-50": highlight,
                    })}
                    placeholder={placeholder || "Click to write..."}
                    value={value || ""}
                    onBlur={() => {
                        triggerChanged();
                    }}
                    onKeyDown={(event) => {
                        if ((event.ctrlKey || event.metaKey) && event.key === "Enter")
                            event.currentTarget.blur();
                    }}
                    onChange={(e) => {
                        setHasBeenEdited(true);
                        setValue(e.target.value);
                    }}
                />
            </div>
        </div>
    );
};

export default EditableTextArea;
